import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Heart, Calendar, Check } from 'lucide-react';
import { toast } from 'sonner';

const typeBadge = (t) => {
  const map = { regular: 'border-blue-200 text-blue-700', visitor: 'border-amber-200 text-amber-700', occasional: 'border-teal-200 text-teal-700', inactive: 'border-slate-300 text-slate-500' };
  return map[t] || 'border-slate-200 text-slate-600';
};

export default function Prayer() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/prayer/today').then(res => setData(res.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleComplete = async (personId) => {
    try {
      await api.post(`/prayer/complete/${personId}`);
      setData(prev => ({
        ...prev,
        assignments: prev.assignments.filter(p => p.person_id !== personId)
      }));
      toast.success('Marked as prayed for');
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="h-8 w-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>;

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="border-slate-200 shadow-sm bg-gradient-to-r from-blue-50 to-slate-50">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="h-6 w-6 text-blue-700" />
            <h2 className="text-2xl font-bold text-slate-900 font-['Outfit']">Prayer List for Today</h2>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-slate-600">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">{today}</span>
              <Badge className="ml-1 text-xs bg-blue-100 text-blue-800 hover:bg-blue-100">{data?.assignments?.length || 0} remaining</Badge>
            </div>
            {data?.assignments?.length > 0 && (
              <Button data-testid="pray-all" variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-50" onClick={async () => {
                try {
                  await api.post('/prayer/complete-all');
                  setData(prev => ({ ...prev, assignments: [] }));
                  toast.success('All prayers marked complete');
                } catch { toast.error('Failed'); }
              }}>
                <Check className="h-3.5 w-3.5 mr-1" />Pray All
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {data?.assignments?.length === 0 && (
        <Card className="border-emerald-200 shadow-sm bg-emerald-50/50">
          <CardContent className="p-8 text-center">
            <Check className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-emerald-900 font-['Outfit']">All prayers completed!</p>
            <p className="text-sm text-emerald-700 mt-1">You've prayed for everyone on today's list.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prayer-assignments">
        {data?.assignments?.map((p, i) => (
          <Card key={p.person_id} className={`border-slate-200 shadow-sm hover:shadow-md transition-shadow animate-fade-in stagger-${Math.min(i + 1, 6)}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <Checkbox
                  data-testid={`prayer-check-${p.person_id}`}
                  onCheckedChange={() => handleComplete(p.person_id)}
                  className="mt-1 border-slate-300 data-[state=checked]:bg-blue-700 data-[state=checked]:border-blue-700"
                />
                <div className="flex items-center gap-3 flex-1">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-lg font-semibold text-blue-800 shrink-0">
                    {p.first_name[0]}{p.last_name[0]}
                  </div>
                  <div>
                    <p className="text-base font-medium text-slate-900">{p.first_name} {p.last_name}</p>
                    <Badge variant="outline" className={`text-xs mt-1 ${typeBadge(p.person_type)}`}>
                      {p.person_type}
                    </Badge>
                  </div>
                </div>
              </div>
              {p.phone && <p className="text-sm text-slate-500 mt-3 ml-10">{p.phone}</p>}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
